import logger from 'src/logger';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import reduce from 'lodash/reduce';
import omit from 'lodash/omit';
import omitBy from 'lodash/omitBy';
import isNil from 'lodash/isNil';
import { replaceTextWithValues, replaceRichText } from 'src/common/utils/text-replace';
import {
  matchDefaultCustomisation,
  matchExtraCustomisation,
} from 'src/client/js/view/utils/match-customisation';
import { findKeyBy, slugs } from 'src/common/config/product';
import qs from 'qs';
import { localiseUrl } from 'src/common/utils/format-localised-url';
import buildProductUrl from '../../../utils/buildProductUrl';
import arrayToSentence from '../../../../../../common/utils/array-to-sentence';

export const getFormatImage = props => {
  const customisations = props.item.customisations;
  const fallbackFormats = get(props, 'fallbackProduct.cart.bookFormats', []);
  const defaultFormats = get(props, 'product.cart.bookFormats', null) || fallbackFormats;
  return (
    defaultFormats
      .filter(defaultFormat =>
        matchDefaultCustomisation(customisations, defaultFormat)
      )
      .find(
        defaultFormat =>
          defaultFormat.bookType === props.item.format.id &&
          matchExtraCustomisation(customisations, defaultFormat)
      ) || {}
  );
};

export const getAddons = props => {
  const fallbackAddons = get(props, 'fallbackProduct.cart.options.cart.addOn', []);
  const defaultAddons = get(props, 'product.cart.options.cart.addOn', []);

  return {
    fallbackAddons,
    defaultAddons
  };
};

export const mergeAddons = (props) => {
  const { fallbackAddons, defaultAddons } = getAddons(props);
  return props.item.components.map(item => {
    let addon = {
      id: item.id,
      image: {
        url: ''
      },
      label: item.id
    };

    const idSplit = item.id.split(':');
    const type = idSplit[0];
    const design = idSplit.pop();
    const byTypeAndDesign = (type, design) => item => item.type === type && item.design === design;

    addon = {
      ...addon,
      ...defaultAddons.find(byTypeAndDesign(type, design)),
      ...fallbackAddons.find(byTypeAndDesign(type, design))
    };

    if (item.id === 'lmn:giftwrap') {
      addon = {
        ...addon,
        ...defaultAddons.find(byTypeAndDesign('giftwrap', 'monkey')),
        ...fallbackAddons.find(byTypeAndDesign('giftwrap', 'monkey'))
      };
    }

    return addon;
  });
};

export const getTitle = props => {
  const fallbackTitle = get(props, 'fallbackProduct.productInformation.title', props.item.sku);
  return get(props, 'product.productInformation.title', null) || fallbackTitle;
};

export const getCustomisations = props => {
  // These values that we're omitting are keys that we dont consider to be specific product customisations
  // such as name, gender, or phototype.
  const customisationsObject = omit(props.item.customisations, ['box', 'components', 'cover', 'locale', 'pageGroup', 'size']);
  if (isEmpty(customisationsObject)) {
    return null;
  }
  if (customisationsObject.characters) {
    customisationsObject.names = arrayToSentence(customisationsObject.characters.map(character => character.name));
  }
  return customisationsObject;
};

export const getCustomisationString = props => {
  const fallbackCustomisation = get(props, 'fallbackProduct.cart.customisation', '');
  const customisationString = get(props, 'product.cart.customisation', null) || fallbackCustomisation;
  const customisationsObject = getCustomisations(props);
  return replaceTextWithValues(customisationString, customisationsObject);
};

export const getType = props => {
  const cartFormatId = props.item.format.id;
  const fallbackBookTypes = get(props, 'product.cart.options.cart.bookType', []);
  const bookTypes = get(props, 'product.cart.options.cart.bookType', null) || fallbackBookTypes;
  const bookType = bookTypes.find(book => book.bookType === cartFormatId);
  return get(bookType, 'label', cartFormatId);
};

export const getExpandedCustomisation = (props, addons = []) => {
  const customisationTranslations = get(props, 'model.prismicGlobalContentModel.orders.customisation', []);
  const customisations = omit(props.item.customisations, ['box', 'components', 'cover']);
  if (isEmpty(customisations)) {
    return [];
  }

  const locale = customisations.locale;
  let translatedLanguage = locale;

  // Getting translated language by locale
  if (get(props, 'product.response.ok', false)) {
    const formBody = get(props, 'product.form.body', []);
    if (!isEmpty(formBody)) {
      const localeSliceType = formBody.find(item => item.sliceType === 'locale');
      if (localeSliceType) {
        // there's no guarantee that localSliceTypeItem will be found
        const localSliceTypeItem = localeSliceType.items.find(item => item.optionValue === locale);
        if (localSliceTypeItem) {
          translatedLanguage = localSliceTypeItem.optionTitle;
        }
      }
    }
  }

  // Getting translated customisations
  const translatedCustomisations = reduce(customisations, (result, value, key) => {
    if (result[key]) {
      return result;
    }
    const translation = customisationTranslations.find(item => {
      return item.id && item.id.includes(key) && item.id.includes(value);
    }) || {};
    result[key] = translation.label || value;
    if (key === 'locale') {
      result.locale = translatedLanguage;
      result.customisationLocale = translatedLanguage;
    }
    return result;
  }, {});

  const fallbackCustomisation = get(props, 'fallbackProduct.cart.expandedCustomisation', []);
  const customisation = get(props, 'product.cart.response.ok', false) ? (
    get(props, 'product.cart.expandedCustomisation', fallbackCustomisation)
  ) : fallbackCustomisation;
  const filteredCustomisation = addons.length > 0 ? customisation : customisation.filter(c => c.text !== '%{addons}');
  const customisationAddon = addons.map(addon => addon.label).join(' + ');
  return replaceRichText(filteredCustomisation, {
    ...translatedCustomisations,
    addons: customisationAddon,
    formatIdentifier: getType(props)
  });
};

export const buildCustomisationsFromItem = item => ({
  ...item.customisations,
  components: item.components,
  format_identifier: item.format.id
});

export const buildEditUrl = ({ item, ...props }, type = 'preview') => {
  const productId = item.format.product.id;
  const productSlug = slugs[findKeyBy.sku(productId)];
  const productCustomisation = buildCustomisationsFromItem(item);
  const query = qs.stringify(productCustomisation, { addQueryPrefix: true });
  const countryModel = props.model.countryModel;
  let url = `/personalized-products/${productSlug}/${type}/${item.id}${query}`;
  if (props.orderNumber) {
    url = `/personalized-products/${productSlug}/${type}/${props.orderNumber}/${item.id}${query}`;
  }
  return localiseUrl(countryModel, url);
};

export const getPrismicProductAndAddons = props => {
  const customisations = omitBy(props.item.customisations, isNil);
  const locale = customisations.locale;
  const countryLocale = props.country.localeCode;
  const countryPrefix = props.country.prefix;
  Promise.all([
    props.getPrismicProduct(props.productId, locale),
    props.getPrismicProduct(props.productId, countryLocale),
    getAddons(props.productId, {
      ...customisations,
      format_identifier: get(props, 'item.format.id'),
      country: countryPrefix
    }),
  ])
    .then(([product, fallbackProduct, addon]) => {
      if (addon.giftwrapPageIdx !== null) {
        props.updateGiftwrapCta({
          edit: props.editGiftwrapCta,
          add: props.addGiftwrapCta,
          url: buildProductUrl(props, {
            type: 'options',
            itemId: props.item.id,
            queryParams: omitBy({
              ...buildCustomisationsFromItem(props.item),
              addonPage: addon.giftwrapPageIdx + 1
            }, isNil)
          }),
        });
      }
      if (addon.extrasPageIdx !== null) {
        props.updateExtraCta({
          edit: props.editExtrasCta,
          add: props.addExtrasCta,
          url: buildProductUrl(props, {
            type: 'options',
            itemId: props.item.id,
            queryParams: {
              ...buildCustomisationsFromItem(props.item),
              addonPage: addon.extrasPageIdx + 1
            }
          }),
        });
      }
      props.setIsLoading(false);
    })
    .catch((error) => {
      props.setIsLoading(false);
      logger.error(error);
    });

};

export const allowEdit = (props) => {
  if (!props.hasCustomisations) {
    return false;
  }
  // Currently MGT, TLB, and TJH removes existing customisations when editing, for this reason we have temporarily
  // removed the possibility to edit them after adding to cart.

  // MSV has no preview, therefore you shouldn't be able to edit it in the first place (this is correct behaviour)
  switch (props.productKey) {
    case 'MGT':
    case 'TLB':
    case 'TJH':
    case 'MSV':
      return false;
  }

  return true;
};




// WEBPACK FOOTER //
// ./src/client/js/view/components/CartItems/LineItem/helpers.js